import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import classes from './Composer.module.css';

/** The textarea styles the mirror copies, so its text lands exactly where the
 *  textarea draws it. */
const MIRRORED = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'lineHeight',
  'letterSpacing',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
] as const;

export interface PixieDustProps {
  readonly text: string;
  readonly textareaRef: RefObject<HTMLTextAreaElement | null>;
}

/**
 * The improve-prompt effect, on the words only. While it runs, the textarea's
 * own text is transparent and this mirror draws the prompt instead, in the
 * same font, padding, width and scroll position, with a band of brand blue
 * sweeping through its letters left to right. Drawing the text once (not
 * over the textarea's) keeps the letters crisp. Timing comes from
 * `--pixie-sweep` on the composer.
 */
export function PixieDust({ text, textareaRef }: PixieDustProps) {
  const mirrorRef = useRef<HTMLDivElement>(null);
  const [mirrorStyle, setMirrorStyle] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!textarea || !mirror) return undefined;
    const computed = getComputedStyle(textarea);
    const style: Record<string, string> = {};
    for (const key of MIRRORED) style[key] = computed[key];
    // The textarea's inner width, without any scrollbar, so the mirror wraps
    // its lines exactly where the textarea does.
    style.width = `${textarea.clientWidth}px`;
    setMirrorStyle(style as CSSProperties);

    const follow = () => {
      mirror.style.transform = `translateY(${-textarea.scrollTop}px)`;
    };
    follow();
    textarea.addEventListener('scroll', follow);
    return () => textarea.removeEventListener('scroll', follow);
  }, [textareaRef]);

  return (
    <div className={classes.pixieDust} aria-hidden>
      <div ref={mirrorRef} className={classes.pixieText} style={mirrorStyle}>
        {text}
      </div>
    </div>
  );
}
